import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../ui/Logo';
import AuthModal from '../common/AuthModal';
import { LANGUAGES } from '../../utils/constants';

const NAV_ITEMS = [
  { label: 'Home',          to: '/' },
  { label: 'Courses',       to: '/courses' },
  { label: 'Tutors',        to: '/tutors' },
  { label: 'For Providers', to: '/providers' },
  { label: 'Case Studies',  to: '/case-studies/malta' },
  { label: 'About',         to: '/about' },
  { label: 'Blog',          to: '/blog' },
];

/** Top nav. variant: 'light' (default) | 'dark' */
export default function Navbar({ variant = 'light' }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const isDark = variant === 'dark';
  const navRef = useRef(null);

  const isActive = (to) => to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(to + '/');

  const openAuth = (initial) => {
    setAuthMode(initial);
    setAuthOpen(true);
    setOpen(false);
  };

  // Close the mobile menu when clicking/tapping outside it
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
    <nav ref={navRef} className="sticky top-0" style={{ zIndex: 40, background: isDark ? 'var(--navy)' : '#fff', borderBottom: `1px solid ${isDark ? 'var(--navy-line)' : 'var(--line)'}` }}>
      <div className="lc-container flex items-center gap-3 sm:gap-6 lg:gap-9" style={{ height: 72 }}>
        <Logo inverted={isDark} />

        {/* Desktop nav */}
        <ul className="hidden lg:flex gap-7 m-0 p-0 list-none ml-6">
          {NAV_ITEMS.map((it) => {
            const active = isActive(it.to);
            return (
              <li key={it.label}>
                <Link
                  to={it.to}
                  className="font-semibold relative whitespace-nowrap"
                  style={{
                    fontSize: 14,
                    padding: '6px 0',
                    color: active ? (isDark ? 'var(--yellow)' : 'var(--green-2)') : (isDark ? 'rgba(255,255,255,0.85)' : 'var(--ink-2)'),
                    borderBottom: active ? `2px solid ${isDark ? 'var(--yellow)' : 'var(--green)'}` : '2px solid transparent',
                  }}
                >{it.label}</Link>
              </li>
            );
          })}
        </ul>

        {/* Right actions */}
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          {/* Desktop-only group (hidden on mobile) */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageDropdown isDark={isDark} />
            <button type="button"
              onClick={() => openAuth('signin')}
              className="font-semibold"
              style={{ fontSize: 14, color: isDark ? '#fff' : 'var(--ink-2)' }}>
              Sign in
            </button>
            <Link to="/courses" className="lc-btn primary sm">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
              Get Started
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setOpen(!open)}
            className="lg:hidden inline-flex items-center justify-center rounded-lg"
            style={{ width: 40, height: 40, background: isDark ? 'rgba(255,255,255,0.1)' : 'var(--bg-soft)' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#fff' : 'var(--ink)'} strokeWidth="2.4">
              {open ? (
                <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
              ) : (
                <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden absolute left-0 right-0 top-full"
          style={{ background: isDark ? 'var(--navy-2)' : '#fff', borderBottom: `1px solid ${isDark ? 'var(--navy-line)' : 'var(--line)'}`, boxShadow: 'var(--shadow-lg)', zIndex: 50, maxHeight: 'calc(100vh - 72px)', overflowY: 'auto' }}>
          <ul className="lc-mobile-menu m-0 p-4 list-none flex flex-col gap-1">
            {NAV_ITEMS.map((it) => {
              const active = isActive(it.to);
              return (
                <li key={it.label}>
                  <Link
                    to={it.to}
                    onClick={() => setOpen(false)}
                    className="block font-semibold"
                    style={{
                      padding: '12px 14px',
                      borderRadius: 10,
                      fontSize: 16,
                      color: active ? (isDark ? 'var(--yellow)' : 'var(--green-2)') : (isDark ? '#fff' : 'var(--ink)'),
                      background: active ? (isDark ? 'rgba(240,184,12,0.1)' : 'rgba(34,181,114,0.08)') : 'transparent',
                    }}
                  >{it.label}</Link>
                </li>
              );
            })}
            <li className="mt-3 pt-3" style={{ borderTop: `1px solid ${isDark ? 'var(--navy-line)' : 'var(--line)'}` }}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-bold uppercase tracking-widest" style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.55)' : 'var(--muted)' }}>Language</span>
              </div>
              <LanguageDropdown isDark={isDark} inline />
            </li>
            <li className="mt-3 pt-3 flex flex-col gap-2" style={{ borderTop: `1px solid ${isDark ? 'var(--navy-line)' : 'var(--line)'}` }}>
              <button type="button" onClick={() => openAuth('signin')} className="lc-btn ghost w-full" style={{ justifyContent: 'center' }}>Sign in</button>
              <Link to="/courses" onClick={() => setOpen(false)} className="lc-btn primary w-full" style={{ justifyContent: 'center' }}>Get Started</Link>
            </li>
          </ul>
        </div>
      )}
    </nav>
    <AuthModal
      isOpen={authOpen}
      onClose={() => setAuthOpen(false)}
      initialMode={authMode}
    />
    </>
  );
}

/**
 * LanguageDropdown — flag + 2-letter code trigger, opens a menu of all
 * supported languages from utils/constants. Visual-only (no i18n wiring);
 * selection persists in component state for now. Swap setCurrent for an
 * i18n setLocale call once the translation layer is in.
 */
function LanguageDropdown({ isDark, inline = false }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(
    () => LANGUAGES.find((l) => l.code === 'en') || LANGUAGES[0]
  );
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref} style={inline ? { width: '100%' } : undefined}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Choose language"
        className="inline-flex items-center gap-1.5 font-semibold rounded-lg"
        style={{
          padding: '7px 12px',
          fontSize: 13,
          color: isDark ? '#fff' : 'var(--ink-2)',
          background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--bg-soft)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--line)'}`,
        }}
      >
        <span
          className={`fi fi-${current.country}`}
          style={{ width: 20, height: 14, borderRadius: 2, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }}
          aria-hidden="true"
        />
        <span className="uppercase tracking-wide">{current.code}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .15s' }}>
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </button>

      {open && (
        <div role="listbox"
          className={inline ? 'rounded-xl overflow-hidden mt-2' : 'absolute right-0 bg-white rounded-xl overflow-hidden'}
          style={inline ? {
            background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--bg-soft)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--line)'}`,
            padding: 6,
            maxHeight: 220,
            overflowY: 'auto',
          } : {
            top: 'calc(100% + 8px)',
            minWidth: 200,
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid var(--line)',
            zIndex: 30,
            padding: 6,
          }}>
          {LANGUAGES.map((l) => {
            const active = l.code === current.code;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { setCurrent(l); setOpen(false); }}
                className="w-full text-left flex items-center gap-2.5 rounded-lg"
                style={{
                  padding: '8px 12px',
                  fontSize: 14,
                  background: active
                    ? (inline && isDark ? 'rgba(255,255,255,0.08)' : 'var(--bg-soft)')
                    : 'transparent',
                  color: inline && isDark ? '#fff' : 'var(--ink)',
                  fontWeight: active ? 600 : 500,
                }}
              >
                <span
                  className={`fi fi-${l.country}`}
                  style={{ width: 22, height: 16, borderRadius: 2, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }}
                  aria-hidden="true"
                />
                <span className="flex-1">{l.label}</span>
                {active && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green-2)" strokeWidth="3">
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
